/**
 * @file useKitchenOps.ts
 * @description Real-time hook synchronizing kitchen inventory, portion decrements, load status, and whiteboard.
 * 
 * ARCHITECTURAL CONTRACTS:
 * 1. Automatically subscribes to Supabase Realtime changes for `items` and `venues`.
 * 2. Provides optimistic updates for instant tactile feedback on kitchen touchscreens.
 * 3. Filters items by kitchen station, text search (name, station, notes), and stock status.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from '../../../config/supabase.config';
import {
  fetchKitchenOpsItems,
  fetchVenueKitchenState,
  updateItemStockStatus,
  updateItemRemainingStock,
  updateItemInternalNotes,
  updateVenueKitchenLoadStatus,
  updateVenueChefNote,
  updateVenueManagerNote,
  parseKitchenWhiteboard,
} from '../services/kitchenOps.service';
import type { 
  KitchenOpsItem, 
  StockStatus, 
  KitchenLoadStatus 
} from '../types/kitchenOps.types';

export const useKitchenOps = (venueId: string | null) => {
  const [items, setItems] = useState<KitchenOpsItem[]>([]);
  const [kitchenLoadStatus, setKitchenLoadStatus] = useState<KitchenLoadStatus>('NORMAL');
  const [managerNote, setManagerNote] = useState<string>('');
  const [chefNote, setChefNote] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedStation, setSelectedStation] = useState<string | null>(null);
  const [stockFilter, setStockFilter] = useState<'ALL' | 'LOW' | 'OUT'>('ALL');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isSavingStatus, setIsSavingStatus] = useState<boolean>(false);
  const [isSavingChefNote, setIsSavingChefNote] = useState<boolean>(false);
  const [isSavingManagerNote, setIsSavingManagerNote] = useState<boolean>(false);
  const [updatingItemId, setUpdatingItemId] = useState<string | null>(null);

  const isMountedRef = useRef(true);

  // Loads initial state for items and venue load/whiteboard
  const loadOpsData = useCallback(async () => {
    if (!venueId) return;
    try {
      setIsLoading(true);
      const [itemsData, venueData] = await Promise.all([
        fetchKitchenOpsItems(venueId),
        fetchVenueKitchenState(venueId),
      ]);
      if (isMountedRef.current) {
        setItems(itemsData);
        setKitchenLoadStatus(venueData.kitchen_load_status);
        setManagerNote(venueData.managerNote);
        setChefNote(venueData.chefNote);
      }
    } catch (err) {
      console.error('[useKitchenOps] Error loading kitchen ops data:', err);
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [venueId]);

  useEffect(() => {
    isMountedRef.current = true;
    if (!venueId) {
      setIsLoading(false);
      return;
    }

    loadOpsData();

    const instanceId = Math.random().toString(36).substring(7);

    // Subscribe to item changes for stock and portion count updates
    const itemChannel = supabase
      .channel(`kitchen-items-${venueId}-${instanceId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'items',
        filter: `venue_id=eq.${venueId}`,
      }, () => {
        loadOpsData();
      })
      .subscribe();

    // Subscribe to venue changes for load status and whiteboard updates
    const venueChannel = supabase
      .channel(`kitchen-venue-${venueId}-${instanceId}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'venues',
        filter: `id=eq.${venueId}`,
      }, (payload) => {
        if (payload.new) {
          const newStatus = (payload.new as any).kitchen_load_status;
          const newWhiteboard = (payload.new as any).kitchen_whiteboard;
          if (newStatus) setKitchenLoadStatus(newStatus);
          if (newWhiteboard !== undefined) {
            const parsed = parseKitchenWhiteboard(newWhiteboard);
            setManagerNote(parsed.managerNote);
            setChefNote(parsed.chefNote);
          }
        }
      })
      .subscribe();

    return () => {
      isMountedRef.current = false;
      supabase.removeChannel(itemChannel);
      supabase.removeChannel(venueChannel);
    };
  }, [venueId, loadOpsData]);

  // Toggle item stock status (IN_STOCK / LOW_STOCK / OUT_OF_STOCK) with optimistic UI update
  const handleToggleStockStatus = async (itemId: string, status: StockStatus) => {
    setUpdatingItemId(itemId);
    const isAvail = status !== 'OUT_OF_STOCK';

    // Optimistic local update
    setItems(prev => prev.map(item => item.id === itemId ? {
      ...item,
      stock_status: status,
      is_available: isAvail,
    } : item));

    try {
      await updateItemStockStatus(itemId, status);
    } catch (err) {
      console.error('[useKitchenOps] Failed to update stock status:', err);
      // Re-fetch to roll back on error
      loadOpsData();
    } finally {
      setUpdatingItemId(null);
    }
  };

  // Increment or decrement remaining portions (-1 / +1)
  const handleAdjustRemainingStock = async (itemId: string, delta: number) => {
    const targetItem = items.find(i => i.id === itemId);
    if (!targetItem) return;

    const currentSettings = (targetItem.availability_settings as any) || {};
    const currentCount = currentSettings.remaining_stock ?? 0;
    const newStock = Math.max(0, currentCount + delta);

    const lowThreshold = currentSettings.low_stock_threshold ?? 5;
    const isOut = newStock <= 0;
    const isLow = newStock <= lowThreshold;
    const newStatus: StockStatus = isOut ? 'OUT_OF_STOCK' : isLow ? 'LOW_STOCK' : 'IN_STOCK';
    const isAvail = !isOut;

    const updatedSettings = {
      ...currentSettings,
      remaining_stock: newStock,
    };

    setUpdatingItemId(itemId);

    // Optimistic update
    setItems(prev => prev.map(i => i.id === itemId ? {
      ...i,
      stock_status: newStatus,
      is_available: isAvail,
      availability_settings: updatedSettings,
    } : i));

    try {
      await updateItemRemainingStock(targetItem, newStock);
    } catch (err) {
      console.error('[useKitchenOps] Failed to adjust portions:', err);
      loadOpsData();
    } finally {
      setUpdatingItemId(null);
    }
  };

  // Update quick item prep notes
  const handleSaveItemNotes = async (itemId: string, notes: string) => {
    setItems(prev => prev.map(i => i.id === itemId ? { ...i, internal_notes: notes } : i));
    try {
      await updateItemInternalNotes(itemId, notes);
    } catch (err) {
      console.error('[useKitchenOps] Failed to save item notes:', err);
      loadOpsData();
    }
  };

  // Change kitchen load status (NORMAL / BUSY / RUSH / OVERLOAD)
  const handleChangeLoadStatus = async (status: KitchenLoadStatus) => {
    if (!venueId) return;
    setIsSavingStatus(true);
    setKitchenLoadStatus(status);
    try {
      await updateVenueKitchenLoadStatus(venueId, status);
    } catch (err) {
      console.error('[useKitchenOps] Failed to change kitchen load status:', err);
      loadOpsData();
    } finally {
      setIsSavingStatus(false);
    }
  };

  // Saves the chef's own line prep notes to the venue whiteboard
  const handleSaveChefNote = async (text: string) => {
    if (!venueId) return;
    setIsSavingChefNote(true);
    setChefNote(text);
    try {
      await updateVenueChefNote(venueId, managerNote, text);
    } catch (err) {
      console.error('[useKitchenOps] Failed to update chef note:', err);
      loadOpsData();
    } finally {
      setIsSavingChefNote(false);
    }
  };

  // Saves the manager's broadcast note (used in management or if edited)
  const handleSaveManagerNote = async (text: string) => {
    if (!venueId) return;
    setIsSavingManagerNote(true);
    setManagerNote(text);
    try {
      await updateVenueManagerNote(venueId, text, chefNote);
    } catch (err) {
      console.error('[useKitchenOps] Failed to update manager note:', err);
      loadOpsData();
    } finally {
      setIsSavingManagerNote(false);
    }
  };

  // Backward-compatible whiteboard handler routing to chef note
  const handleSaveWhiteboard = async (text: string) => {
    return handleSaveChefNote(text);
  };

  // Distinct kitchen stations configured across venue items
  const stations = useMemo(() => {
    const list = Array.from(new Set(items.map(i => i.kitchen_station).filter(Boolean))) as string[];
    return list;
  }, [items]);

  // Filtered items list
  const filteredItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return items.filter(item => {
      // 1. Station match
      if (selectedStation && item.kitchen_station !== selectedStation) {
        return false;
      }
      // 2. Stock status filter
      if (stockFilter === 'LOW' && item.stock_status !== 'LOW_STOCK') {
        return false;
      }
      if (stockFilter === 'OUT' && item.stock_status !== 'OUT_OF_STOCK') {
        return false;
      }
      // 3. Search query
      if (q) {
        const matchesName = item.name.toLowerCase().includes(q);
        const matchesStation = Boolean(item.kitchen_station?.toLowerCase().includes(q));
        const matchesNotes = Boolean(item.internal_notes?.toLowerCase().includes(q));
        if (!matchesName && !matchesStation && !matchesNotes) {
          return false;
        }
      }
      return true;
    });
  }, [items, selectedStation, stockFilter, searchQuery]);

  const outOfStockCount = useMemo(() => {
    return items.filter(i => i.stock_status === 'OUT_OF_STOCK').length;
  }, [items]);

  const lowStockCount = useMemo(() => {
    return items.filter(i => i.stock_status === 'LOW_STOCK').length;
  }, [items]);

  return {
    items,
    filteredItems,
    stations,
    kitchenLoadStatus,
    managerNote,
    chefNote,
    whiteboardText: chefNote || managerNote,
    isLoading,
    isSavingStatus,
    isSavingChefNote,
    isSavingManagerNote,
    isSavingWhiteboard: isSavingChefNote || isSavingManagerNote,
    updatingItemId,
    selectedStation,
    stockFilter,
    searchQuery,
    outOfStockCount,
    lowStockCount,
    setSelectedStation,
    setStockFilter,
    setSearchQuery,
    setManagerNote,
    setChefNote,
    setWhiteboardText: setChefNote,
    handleToggleStockStatus,
    handleAdjustRemainingStock,
    handleSaveItemNotes,
    handleChangeLoadStatus,
    handleSaveChefNote,
    handleSaveManagerNote,
    handleSaveWhiteboard,
    refreshOpsData: loadOpsData,
  };
};
